import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Loader2, Plane, Siren } from 'lucide-react';
import { useNow } from '@/components/allocation/use-now';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Can } from '@/lib/access';
import { cn } from '@/lib/cn';
import type { IFlightBoardSummary, IFlightMonitorRow } from '@/lib/flights/types';
import { InfiniteScrollSentinel } from '@/lib/pagination/infinite-scroll-sentinel';
import { joinFlights, leaveFlights } from '@/lib/ws/socket';
import { FlightsFilterBar } from './filters';
import { BOARD_GRID, BOARD_GRID_SELECT, FlightRowItem } from './flight-row';
import {
  goneFromBoard,
  nowLineIndex,
  riskIndicator,
  selectDisabledReason,
  summarizeBoard,
  type BoardSummary,
} from './status';
import { useFlightsList } from './use-flights-list';
import { hourBucketOf, useClockBasis, type ClockBasis } from './clock-basis';
import { ClockSwitch } from './clock-switch';
import { NowLine } from './now-line';
import { ZoneNote } from './zone-note';
import { declareFlightsState } from '@/routes/declare/declare-route.state';

// /flights as a DEPARTURES BOARD (NEX-342 / F4 restyle): the operator's current
// flights rendered like an airport FIDS — dense mono rows, loud status column,
// hour separators, a live clock, and a clickable status summary strip. Token-
// native surface (white-label theming holds; §18.4 — no hardcoded panel).
//
// Data path unchanged: cursor-paginated infinite list in its OWN bounded scroll
// region (§18.1), kept live by the `flights` WS room (NEX-343 / F5) — each
// `flight.status-changed` push merges into the list cache via applyEvent; the
// status cell flashes on change. No polling anywhere.
//
// TODAY (2026-10-26, product owner): without a date filter the board is today's at each
// airport, minus what already left the operator's airport or landed (`?board=today`,
// the BFF's `today-board.ts`), and a «Ahora HH:MM» line splits what should have left by
// now — delayed, cancelled, in the air — from the flights still to come.
//
// The board does NOT open cases (that path is gone — a case only exists under a
// declared disruption). Instead it multi-SELECTS: each row carries a checkbox,
// the selection surfaces a "Start disruption" bar, and that action is a DOOR into
// the one declare flow (unified-declare U6): it hands the selected flight URNs to
// `/express` as router state, so the flow skips step 1 and opens step 2 with them.
// The board itself declares nothing.

/** Multi-select plumbing the board hands to the list body / rows. */
export interface BoardSelection {
  selectedUrns: ReadonlySet<string>;
  onToggle: (row: IFlightMonitorRow) => void;
  /** Header select-all: set (or clear) a whole batch of rows at once. */
  onSetAll: (rows: IFlightMonitorRow[], next: boolean) => void;
}

export function FlightsRoute() {
  const { t } = useTranslation('network');
  const flights = useFlightsList();
  const [params] = useSearchParams();
  const [clock, setClock] = useClockBasis();

  // `?risk=high` — the At-risk chip's client-side lens: narrows the LOADED rows
  // to the high predictor band. Client-side on purpose — risk is enriched per
  // page (predictor budget), so this lens and the chip's count cover the same set.
  const riskLens = params.get('risk') === 'high';
  // A live push may have made a row gone (departed from here, landed) — off the board now.
  const live = flights.items.filter((r) => !goneFromBoard(r));
  const rows = riskLens
    ? live.filter((r) => riskIndicator(r.cancelProbability, r.expectedDelayMinutes)?.band === 'high')
    : live;
  // «Ahora» moves with the clock, not with the data (a minute is the board's grain).
  const now = useNow(flights.today, 30_000);

  // The multi-select feeding "Start disruption" — flight URNs in selection order.
  // URNs survive a filter change that scrolls their rows out of the loaded window;
  // the declare flow resolves the flights itself.
  const navigate = useNavigate();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const selection: BoardSelection = useMemo(
    () => ({
      selectedUrns: selected,
      onToggle: (row) =>
        setSelected((prev) => {
          const next = new Set(prev);
          if (next.has(row.flightUrn)) next.delete(row.flightUrn);
          else next.add(row.flightUrn);
          return next;
        }),
      onSetAll: (batch, on) =>
        setSelected((prev) => {
          const next = new Set(prev);
          for (const row of batch) {
            if (on) next.add(row.flightUrn);
            else next.delete(row.flightUrn);
          }
          return next;
        }),
    }),
    [selected],
  );
  const clearSelection = (): void => setSelected(new Set());
  // Router state, never query params (declare-route.state.ts): nothing about a
  // flight leaks into the URL, and a refresh lands on step 1 by design.
  const startDisruption = (): void => {
    if (selected.size === 0) return;
    navigate('/express', { state: declareFlightsState([...selected]) });
  };

  // Subscribe to the cluster-wide flights room for the lifetime of the board.
  // Safe no-op when the socket isn't connected (offline/tests).
  useEffect(() => {
    joinFlights();
    return () => leaveFlights();
  }, []);

  return (
    <main className="density-page flex h-full min-h-0 w-full flex-col gap-3 p-4 lg:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('board.title')}</h1>
          {flights.today ? (
            <p className="mt-0.5 text-xs text-muted-foreground" data-testid="board-today-hint">
              {t('board.todayHint')}
            </p>
          ) : null}
          <ZoneNote />
        </div>
        <div className="flex items-end gap-3">
          <ClockSwitch clock={clock} onChange={setClock} />
          <BoardClock />
        </div>
      </header>

      <SummaryStrip rows={live} serverSummary={flights.summary} today={flights.today} />
      <FlightsFilterBar />

      <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card">
        <FlightsListBody
          isLoading={flights.isLoading}
          isError={flights.isError}
          rows={rows}
          hasNextPage={flights.hasNextPage}
          isFetchingNextPage={flights.isFetchingNextPage}
          fetchNextPage={flights.fetchNextPage}
          isStale={flights.isStale}
          selection={selection}
          {...(flights.today ? { nowLine: now } : {})}
          clock={clock}
        />
      </section>

      {selected.size > 0 ? (
        <div
          className="flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2"
          data-testid="flights-selection-bar"
        >
          <p className="text-sm">
            <span className="font-semibold tabular-nums">{selected.size}</span>{' '}
            {t('board.selection.selected', { count: selected.size })}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={clearSelection}>
              {t('board.selection.clear')}
            </Button>
            {/* Same permission the /express route guard checks: an operator who
                cannot declare is never offered a door into the declare flow. */}
            <Can permission="disruptions.declare">
              <Button
                size="sm"
                onClick={startDisruption}
                data-testid="flights-start-disruption"
              >
                <Siren className="h-4 w-4" aria-hidden />
                {t('board.selection.start')}
              </Button>
            </Can>
          </div>
        </div>
      ) : null}
    </main>
  );
}

/* ── Live clock (presentation only — the DATA liveness is the WS room) ──────── */

function BoardClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="text-right" data-testid="board-clock">
      <p className="font-mono text-xl font-semibold tabular-nums leading-none">
        {now.toLocaleTimeString(undefined, { hour12: false })}
      </p>
      <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {now.toLocaleDateString(undefined, { day: '2-digit', month: 'short' })} ·{' '}
        {now.toISOString().slice(11, 16)} UTC
      </p>
    </div>
  );
}

/* ── Summary strip — server counts over the WHOLE board, chips filter by status ─ */

const SUMMARY_CHIPS: {
  key: keyof BoardSummary;
  /** `network` key for the chip caption — the strip renders it with t(). */
  labelKey: string;
  status: string;
  toneClass: string;
}[] = [
  { key: 'onTime', labelKey: 'board.summary.onTime', status: 'SCHEDULED', toneClass: 'text-emerald-600 dark:text-emerald-400' },
  { key: 'delayed', labelKey: 'board.summary.delayed', status: 'DELAYED', toneClass: 'text-amber-600 dark:text-amber-400' },
  { key: 'cancelled', labelKey: 'board.summary.cancelled', status: 'CANCELLED', toneClass: 'text-rose-600 dark:text-rose-400' },
  { key: 'departed', labelKey: 'board.summary.departed', status: 'DEPARTED', toneClass: 'text-sky-600 dark:text-sky-400' },
  { key: 'arrived', labelKey: 'board.summary.arrived', status: 'ARRIVED', toneClass: 'text-muted-foreground' },
];

/**
 * The status chips count the WHOLE filtered board (server fold riding the
 * monitor envelope — the loaded page used to be the count, so an off-page
 * DELAYED flight read 0 until you clicked its filter). Client fold stays as the
 * fallback for an older BFF. "At risk" is the exception by design: risk is
 * enriched per page (predictor budget), so its count covers the LOADED rows and
 * its chip toggles a client-side lens (`?risk=high`) over them.
 */
function SummaryStrip({
  rows,
  serverSummary,
  today,
}: {
  rows: IFlightMonitorRow[];
  serverSummary?: IFlightBoardSummary | undefined;
  /** Today's board shows no landed flight — its «Arribados» chip would always read 0. */
  today: boolean;
}) {
  const { t } = useTranslation('network');
  const [params, setParams] = useSearchParams();
  const loaded = summarizeBoard(rows);
  const counts: BoardSummary = serverSummary
    ? {
        onTime: serverSummary.scheduled,
        delayed: serverSummary.delayed,
        departed: serverSummary.departed,
        arrived: serverSummary.arrived,
        cancelled: serverSummary.cancelled,
        atRisk: loaded.atRisk,
      }
    : loaded;
  const active = params.get('status') ?? '';
  const riskLens = params.get('risk') === 'high';

  const toggle = (key: 'status' | 'risk', value: string, current: string): void => {
    const next = new URLSearchParams(params);
    if (current === value) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="board-summary">
      {SUMMARY_CHIPS.filter((chip) => !(today && chip.key === 'arrived')).map((chip) => (
        <button
          key={chip.key}
          type="button"
          onClick={() => toggle('status', chip.status, active)}
          aria-pressed={active === chip.status}
          data-testid={`board-summary-${chip.key}`}
          className={cn(
            'flex items-center gap-1.5 rounded-md border px-2 py-1 transition-colors',
            active === chip.status
              ? 'border-primary bg-primary/10'
              : 'border-border hover:bg-muted',
          )}
        >
          <span className={cn('font-mono text-sm font-bold tabular-nums', chip.toneClass)}>
            {counts[chip.key]}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {t(chip.labelKey)}
          </span>
        </button>
      ))}
      <button
        type="button"
        onClick={() => toggle('risk', 'high', riskLens ? 'high' : '')}
        aria-pressed={riskLens}
        data-testid="board-summary-atRisk"
        title={t('board.summary.atRiskHint')}
        className={cn(
          'flex items-center gap-1.5 rounded-md border px-2 py-1 transition-colors',
          riskLens ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted',
        )}
      >
        <span className="font-mono text-sm font-bold tabular-nums text-rose-600 dark:text-rose-400">
          {counts.atRisk}
        </span>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {t('board.summary.atRisk')}
        </span>
      </button>
    </div>
  );
}

/* ── Board body — sticky column header, hour separators, bounded scroll ─────── */

interface FlightsListBodyProps {
  isLoading: boolean;
  isError: boolean;
  rows: IFlightMonitorRow[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  /** Pick mode (the Express flow): whole-row select — see FlightRowItem. */
  onSelectRow?: (row: IFlightMonitorRow) => void;
  /** Select mode (the /flights board): per-row checkboxes + header select-all. */
  selection?: BoardSelection;
  /**
   * The rows belong to the PREVIOUS filter while the new one loads
   * (useInfiniteList `isStale`). The board dims instead of blanking to a
   * skeleton — a filter change reads as narrowing, not as a reload.
   */
  isStale?: boolean;
  /** Today's board: «Ahora» (this instant) drawn before the first flight still to leave. */
  nowLine?: number;
  /** The board's clock: the time set big and the hour separators' (`clock-basis.ts`). */
  clock?: ClockBasis;
}

// Issue #662: Hour bucket now uses the departure airport's timezone.
// A LIM flight at 23:50 local (04:50Z) shows under "Oct 08 · 23:00"
// instead of "Oct 09 · 01:00" (Buenos Aires browser time).

export function FlightsListBody({
  isLoading,
  isError,
  rows,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  onSelectRow,
  selection,
  isStale = false,
  nowLine,
  clock = 'local',
}: FlightsListBodyProps) {
  const { t } = useTranslation('network');
  if (isLoading) {
    return (
      <div className="space-y-1.5 p-4" data-testid="flights-loading">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }
  if (isError) {
    return <div className="p-6 text-sm text-destructive">{t('board.loadError')}</div>;
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          icon={<Plane className="h-6 w-6" />}
          title={t(nowLine === undefined ? 'board.empty' : 'board.emptyToday')}
        />
      </div>
    );
  }

  // Interleave hour separators between rows (render order — the roster arrives
  // departure-sorted; a sparse/unsorted upstream just yields more separators).
  // Issue #662: The hour bucket uses the departure airport's timezone so the
  // separator matches the row times (e.g., LIM flight at 23:50 local shows
  // under "Oct 08 · 23:00", not "Oct 09 · 01:00" in a Buenos Aires browser).
  const board: ReactNode[] = [];
  let lastHour: string | null = null;
  // «Ahora»: before the first flight still to leave — or after the last loaded one when
  // none is left (only once every page is in, else the next page may hold them).
  const nowAt = nowLine === undefined ? -1 : nowLineIndex(rows, nowLine);
  const nowRow =
    nowLine === undefined ? null : <NowLine key="now-line" now={nowLine} none={nowAt === rows.length} />;
  rows.forEach((row, i) => {
    if (i === nowAt) board.push(nowRow);
    // The separators read in the board's clock: the operator's own by default — one clock
    // for every row, so they follow the real order (PO 2026-10-28) — or, switched, each
    // departure airport's with its code (Issue #662).
    const hour = hourBucketOf(row.scheduledDepartureUtc, row.origin, clock);
    if (hour && hour !== lastHour) {
      board.push(
        <li
          key={`sep-${hour}`}
          className="border-t border-border bg-muted/40 px-3 py-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
          data-testid="board-hour-separator"
        >
          {hour}
        </li>,
      );
      lastHour = hour;
    }
    board.push(
      <li key={row.flightUrn}>
        <FlightRowItem
          row={row}
          clock={clock}
          {...(onSelectRow ? { onSelect: onSelectRow } : {})}
          {...(selection && !onSelectRow ? { selection } : {})}
        />
      </li>,
    );
  });
  if (nowAt === rows.length && !hasNextPage) board.push(nowRow);

  // Header select-all — over the LOADED selectable rows only (already-covered /
  // already-arrived flights never join, so "all" means "all that could").
  const selectMode = !!selection && !onSelectRow;
  const selectableRows = selectMode ? rows.filter((r) => !selectDisabledReason(r)) : [];
  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every((r) => selection!.selectedUrns.has(r.flightUrn));
  const someSelected = selectableRows.some((r) => selection!.selectedUrns.has(r.flightUrn));

  // The list is the ONLY scroll region (§18.1) — bounded, independent of the
  // page. The sentinel + loading row live INSIDE it.
  return (
    <div
      className={cn(
        'min-h-0 flex-1 overflow-y-auto transition-opacity',
        isStale && 'opacity-60',
      )}
      data-testid="flights-scroll-region"
      data-stale={isStale ? 'true' : undefined}
      aria-busy={isStale || undefined}
    >
      <div
        className={cn(
          selectMode ? BOARD_GRID_SELECT : BOARD_GRID,
          'sticky top-0 z-10 border-b border-border bg-card px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground',
        )}
        // The header is decorative EXCEPT in select mode, where it hosts the
        // (interactive, labelled) select-all checkbox.
        {...(selectMode ? {} : { 'aria-hidden': true })}
      >
        {selectMode ? (
          <input
            type="checkbox"
            className="h-3.5 w-3.5 accent-primary disabled:cursor-not-allowed disabled:opacity-30"
            checked={allSelected}
            disabled={selectableRows.length === 0}
            ref={(el) => {
              if (el) el.indeterminate = someSelected && !allSelected;
            }}
            onChange={() => selection!.onSetAll(selectableRows, !allSelected)}
            aria-label={t('board.selectAll')}
            title={t('board.selectAll')}
            data-testid="flights-select-all"
          />
        ) : null}
        <span>{t(clock === 'origin' ? 'board.columns.timeOrigin' : 'board.columns.timeLocal')}</span>
        <span className="hidden sm:block">
          {t(clock === 'origin' ? 'board.columns.timeLocal' : 'board.columns.timeOrigin')}
        </span>
        <span>{t('board.columns.flight')}</span>
        <span>{t('board.columns.route')}</span>
        <span className="hidden sm:block" aria-hidden />
        <span className="hidden sm:block">{t('board.columns.equipment')}</span>
        <span className="hidden sm:block">{t('board.columns.pax')}</span>
        <span className="hidden sm:block" title={t('board.riskHint')}>
          {t('board.columns.risk')}
        </span>
        <span>{t('board.columns.status')}</span>
        <span />
      </div>
      <ul className="divide-y divide-border/60" data-testid="flights-list">
        {board}
      </ul>
      {isFetchingNextPage ? (
        <div
          className="flex items-center justify-center gap-2 p-3 text-xs text-muted-foreground"
          data-testid="flights-loading-more"
        >
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          {t('board.loadingMore')}
        </div>
      ) : null}
      <InfiniteScrollSentinel
        onReachEnd={fetchNextPage}
        disabled={!hasNextPage || isFetchingNextPage}
      />
    </div>
  );
}
